import { PermissionCheck } from '@/components';
import { CoreApi, ReleasesApi } from '@/state/api';
import { ReleasesType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import { normalizeKey } from '@/utils/utilFunctions';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Separator,
  Span,
  Text,
} from '@chakra-ui/react';
import {
  Alert,
  Card,
  Checkbox,
  FileUpload,
  Input,
  OverlayDialog,
  SelectWithAction,
  Spinner,
  Switch,
  toasterApi,
} from '@ui';
import React, { useEffect, useMemo, useState } from 'react';
import { FiUploadCloud } from 'react-icons/fi';
import z from 'zod';

type WorkbookSheetCandidate = {
  row_index: number;
  score: number;
  non_empty_count: number;
  preview: string[];
};

type WorkbookSheetInfo = {
  name: string;
  index: number;
  detected_header_row_index: number;
  header_candidates: WorkbookSheetCandidate[];
  headers: string[];
  is_recommended: boolean;
};

type WorkbookInspectResponse = {
  sheet_names: string[];
  recommended_sheet_name: string;
  recommended_header_row_index: number;
  sheets: WorkbookSheetInfo[];
};

type PresignedUploadItem = {
  filename: string;
  upload_url: string;
  file_url: string;
};

type BomMappingForm = ReleasesType.BOMMappings & {
  id?: number;
  sheet_name: string;
  header_row_index: number;
};

type ItemTypeOption = {
  label: string;
  value: string;
};

const DEFAULT_MAPPING: ReleasesType.BOMMappings['mapping'] = {
  part_number: {
    source: '',
    data_type: ReleasesType.itemType.String,
    part_attribute: true,
    summary_view: true,
  },
  total_part_qty: {
    source: '',
    data_type: ReleasesType.itemType.Integer,
    part_attribute: false,
    summary_view: true,
  },
};

const buildUploadFilename = (file: File) => {
  const timestamp = Date.now();
  const safeName = file.name.replace(/\s+/g, '_');
  return `bom_mapping_${timestamp}_${safeName}`;
};

const uploadFileToPresignedUrl = async (
  file: File,
  presigned: PresignedUploadItem,
) => {
  const response = await fetch(presigned.upload_url, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload ${file.name} to S3`);
  }

  return presigned.file_url;
};

const formatCandidateLabel = (candidate: WorkbookSheetCandidate) => {
  const preview = candidate.preview?.slice(0, 4).join(' | ') || 'No preview';
  return `Row ${candidate.row_index + 1} — ${preview}`;
};

const getDefaultItemTypeForKey = (key: string) => {
  if (key === 'part_number') return ReleasesType.itemType.String;
  if (key === 'total_part_qty') return ReleasesType.itemType.Integer;
  return ReleasesType.itemType.String;
};

const normalizeItemTypeOptions = (): ItemTypeOption[] => {
  const rawOptions = ReleasesType.itemTypeEnums ?? [];

  return rawOptions
    .map((option: any) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        };
      }

      return {
        label: String(option?.label ?? option?.value ?? ''),
        value: String(option?.value ?? option?.label ?? ''),
      };
    })
    .filter((option) => option.label && option.value);
};

const getItemTypeLabel = (value?: string | null) => {
  const options = normalizeItemTypeOptions();
  const matched = options.find(
    (option) => option.value === value || option.label === value,
  );

  return matched?.label ?? value ?? '';
};

const getItemTypeValueFromSelection = (selection: string) => {
  const options = normalizeItemTypeOptions();
  const matched = options.find(
    (option) => option.label === selection || option.value === selection,
  );

  return matched?.value ?? selection;
};

const ensureMappingDefaults = (
  mapping?: ReleasesType.BOMMappings['mapping'],
): ReleasesType.BOMMappings['mapping'] => {
  const base = mapping ?? {};
  const normalized: ReleasesType.BOMMappings['mapping'] = {};

  Object.entries(base).forEach(([key, item]) => {
    normalized[key] = {
      source: item?.source ?? '',
      data_type:
        item?.data_type ??
        (getDefaultItemTypeForKey(key) as (typeof ReleasesType.itemType)[keyof typeof ReleasesType.itemType]),
      required: Boolean(item?.required),
      unit: item?.unit ?? '',
      part_attribute:
        typeof item?.part_attribute === 'boolean'
          ? item.part_attribute
          : key === 'part_number',
      summary_view:
        typeof item?.summary_view === 'boolean' ? item.summary_view : true,
    };
  });

  if (!normalized.part_number) {
    normalized.part_number = { ...DEFAULT_MAPPING.part_number };
  } else {
    normalized.part_number = {
      ...DEFAULT_MAPPING.part_number,
      ...normalized.part_number,
      data_type: ReleasesType.itemType.String,
    };
  }

  if (!normalized.total_part_qty) {
    normalized.total_part_qty = { ...DEFAULT_MAPPING.total_part_qty };
  } else {
    normalized.total_part_qty = {
      ...DEFAULT_MAPPING.total_part_qty,
      ...normalized.total_part_qty,
      data_type: ReleasesType.itemType.Integer,
    };
  }

  return normalized;
};

const rebuildMappingFromHeaders = (
  headers: string[],
  selectedPartNumber: string | null,
  selectedTotalQuantity: string | null,
  previousMapping?: ReleasesType.BOMMappings['mapping'],
) => {
  const normalizedPrevious = ensureMappingDefaults(previousMapping);

  const nextMapping: ReleasesType.BOMMappings['mapping'] = {
    part_number: {
      ...DEFAULT_MAPPING.part_number,
      ...(normalizedPrevious?.part_number ?? {}),
      source: selectedPartNumber || '',
      data_type: ReleasesType.itemType.String,
      part_attribute: true,
      summary_view: true,
    },
    total_part_qty: {
      ...DEFAULT_MAPPING.total_part_qty,
      ...(normalizedPrevious?.total_part_qty ?? {}),
      source: selectedTotalQuantity || '',
      data_type: ReleasesType.itemType.Integer,
      part_attribute: false,
      summary_view: true,
    },
  };

  headers.forEach((header) => {
    if (!header) return;
    if (header === selectedPartNumber || header === selectedTotalQuantity) return;

    const keyBase = normalizeKey(header);
    let uniqueKey = keyBase;
    let counter = 1;

    while (nextMapping[uniqueKey]) {
      uniqueKey = `${keyBase}_${counter}`;
      counter += 1;
    }

    const prevKey = Object.keys(normalizedPrevious ?? {}).find(
      (k) => normalizedPrevious?.[k]?.source === header,
    );
    const prevItem = prevKey ? normalizedPrevious?.[prevKey] : undefined;

    nextMapping[uniqueKey] = {
      source: header,
      data_type: prevItem?.data_type ?? ReleasesType.itemType.String,
      required: Boolean(prevItem?.required),
      unit: prevItem?.unit ?? '',
      part_attribute: Boolean(prevItem?.part_attribute),
      summary_view:
        typeof prevItem?.summary_view === 'boolean'
          ? prevItem.summary_view
          : true,
    };
  });

  return nextMapping;
};

const StyledNativeSelect = ({
  value,
  options,
  placeholder = 'Select an option',
  onChange,
  disabled = false,
}: {
  value?: string;
  options: ItemTypeOption[];
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const safeValue = value ?? '';

  return (
    <Box
      width="100%"
      border="1px solid"
      borderColor="border.emphasized"
      borderRadius="md"
      bg="transparent"
      height="40px"
      display="flex"
      alignItems="center"
      paddingX={3}
    >
      <Box
        as="select"
        value={safeValue}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value)
        }
        disabled={disabled}
        width="100%"
        height="100%"
        bg="transparent"
        color={safeValue ? 'inherit' : 'fg.muted'}
        border="none"
        outline="none"
        fontSize="sm"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        sx={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option
            key={`${option.value}-${option.label}`}
            value={option.value}
            style={{ color: 'black' }}
          >
            {option.label}
          </option>
        ))}
      </Box>
    </Box>
  );
};

const BomMappingTable = ({
  mapping,
  readOnly = false,
  onUpdateField,
}: {
  mapping: BomMappingForm['mapping'];
  readOnly?: boolean;
  onUpdateField?: <
    K extends keyof ReleasesType.BOMMappings['mapping'][string],
  >(
    key: string,
    field: K,
    value: ReleasesType.BOMMappings['mapping'][string][K],
  ) => void;
}) => {
  const normalizedMapping = useMemo(() => ensureMappingDefaults(mapping), [mapping]);

  const visibleEntries = useMemo(
    () =>
      Object.entries(normalizedMapping).filter(
        ([key]) => key !== 'part_number' && key !== 'total_part_qty',
      ),
    [normalizedMapping],
  );

  const itemTypeOptions = useMemo(() => normalizeItemTypeOptions(), []);

  return (
    <Flex
      flexDirection="column"
      border="1px solid"
      borderColor="fg"
      padding={2}
      borderRadius="sm"
      gapY={2}
    >
      <Flex gapX={8}>
        <Flex flex={2}>
          <Text fontWeight="bold" fontSize="sm">
            Column Name
          </Text>
        </Flex>
        <Flex flex={1}>
          <Text fontWeight="bold" fontSize="sm">
            Required
          </Text>
        </Flex>
        <Flex flex={2}>
          <Text fontWeight="bold" fontSize="sm">
            Item Type
          </Text>
        </Flex>
        <Flex flex={2}>
          <Text fontWeight="bold" fontSize="sm">
            Unit (optional)
          </Text>
        </Flex>
        <Flex flex={1}>
          <Text fontWeight="bold" fontSize="sm">
            Part Attribute
          </Text>
        </Flex>
      </Flex>

      <Separator />

      {!visibleEntries.length ? (
        <Flex padding={2}>
          <Text>No additional mapped columns</Text>
        </Flex>
      ) : (
        visibleEntries.map(([key, item], index) => {
          const selectedType =
            item?.data_type ?? ReleasesType.itemType.String;

          const selectedTypeLabel = getItemTypeLabel(selectedType);

          return (
            <Flex key={`${key}-${index}`} flexDirection="column" gapY={2}>
              <Flex alignItems="center" gapX={8}>
                <Flex flex={2}>
                  <Text fontWeight="bold" fontSize="sm">
                    {item?.source || key}
                  </Text>
                </Flex>

                <Flex flex={1}>
                  {readOnly ? (
                    <Text fontSize="sm">{item?.required ? 'Yes' : 'No'}</Text>
                  ) : (
                    <Switch
                      value={Boolean(item?.required)}
                      onValueChange={(newValue: boolean) => {
                        onUpdateField?.(key, 'required', newValue);
                      }}
                    />
                  )}
                </Flex>

                <Flex flex={2}>
                  {readOnly ? (
                    <Text fontSize="sm">{selectedTypeLabel || '-'}</Text>
                  ) : (
                    <StyledNativeSelect
                      value={String(selectedType || '')}
                      options={itemTypeOptions}
                      placeholder="Select an option"
                      onChange={(newValue: string) => {
                        onUpdateField?.(
                          key,
                          'data_type',
                          getItemTypeValueFromSelection(
                            newValue,
                          ) as (typeof ReleasesType.itemType)[keyof typeof ReleasesType.itemType],
                        );
                      }}
                    />
                  )}
                </Flex>

                <Flex flex={2}>
                  {readOnly ? (
                    <Text fontSize="sm">{item?.unit || '-'}</Text>
                  ) : (
                    <Input
                      label=""
                      minWidth="80%"
                      value={item?.unit || ''}
                      onChange={(newValue: string) => {
                        onUpdateField?.(key, 'unit', newValue);
                      }}
                    />
                  )}
                </Flex>

                <Flex flex={1}>
                  {readOnly ? (
                    <Text fontSize="sm">
                      {item?.part_attribute ? 'Yes' : 'No'}
                    </Text>
                  ) : (
                    <Checkbox
                      checked={Boolean(item?.part_attribute)}
                      inputProps={{
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          onUpdateField?.(
                            key,
                            'part_attribute',
                            e.target.checked,
                          );
                        },
                      }}
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
          );
        })
      )}
    </Flex>
  );
};

const BomMapping = () => {
  const [activeTab, setActiveTab] = useState<ReleasesType.BomMappingType>(
    ReleasesType.bomMappingType.Part,
  );

  return (
    <Flex flexDirection="column" gapY={4}>
      <Heading fontSize="md">BOM Mapping</Heading>

      <Flex gapX={2}>
        <Button
          size="sm"
          variant={
            activeTab === ReleasesType.bomMappingType.Part ? 'solid' : 'outline'
          }
          color={activeTab === ReleasesType.bomMappingType.Part ? undefined : 'fg'}
          borderColor="fg"
          data-testid="tutorial-bom-mapping-part-tab"
          onClick={() => setActiveTab(ReleasesType.bomMappingType.Part)}
        >
          Part BOM Mappings
        </Button>
        <Button
          size="sm"
          variant={
            activeTab === ReleasesType.bomMappingType.Frame ? 'solid' : 'outline'
          }
          color={activeTab === ReleasesType.bomMappingType.Frame ? undefined : 'fg'}
          borderColor="fg"
          data-testid="tutorial-bom-mapping-frame-tab"
          onClick={() => setActiveTab(ReleasesType.bomMappingType.Frame)}
        >
          Frame BOM Mappings
        </Button>
      </Flex>

      {activeTab === ReleasesType.bomMappingType.Part ? (
        <PartBomMappingList />
      ) : (
        <FrameBomMappingList />
      )}
    </Flex>
  );
};

export default BomMapping;

const PartBomMappingList = () => {
  const handleCreateBomMapping = () => {
    OverlayDialog.open('createBomMapping', {
      overlayId: 'createBomMapping',
      title: 'New Part BOM Mapping',
      description:
        'Upload an Excel file, choose the sheet/header row, then configure how each column should be stored and displayed.',
      content: <CreateBomMappingOverlayDialogue overlayId="createBomMapping" />,
    });
  };

  const handleViewBomMapping = (bomMapping: BomMappingForm) => {
    OverlayDialog.open(`viewBomMapping-${bomMapping.id}`, {
      overlayId: `viewBomMapping-${bomMapping.id}`,
      title: `View BOM Mapping`,
      description: bomMapping.name,
      content: (
        <ViewBomMappingOverlayDialogue
          overlayId={`viewBomMapping-${bomMapping.id}`}
          bomMapping={bomMapping}
        />
      ),
    });
  };

  const handleEditBomMapping = (bomMapping: BomMappingForm) => {
    OverlayDialog.open(`editBomMapping-${bomMapping.id}`, {
      overlayId: `editBomMapping-${bomMapping.id}`,
      title: `Edit BOM Mapping`,
      description: bomMapping.name,
      content: (
        <EditBomMappingOverlayDialogue
          overlayId={`editBomMapping-${bomMapping.id}`}
          bomMapping={bomMapping}
        />
      ),
    });
  };

  const handleDeleteBomMapping = (bomMapping: BomMappingForm) => {
    OverlayDialog.open(`deleteBomMapping-${bomMapping.id}`, {
      overlayId: `deleteBomMapping-${bomMapping.id}`,
      title: 'Delete BOM Mapping',
      description: `Are you sure you want to delete "${bomMapping.name}"?`,
      content: (
        <DeleteBomMappingOverlayDialogue
          overlayId={`deleteBomMapping-${bomMapping.id}`}
          bomMapping={bomMapping}
        />
      ),
    });
  };

  const { data: bomMappings } = ReleasesApi.Api.useGetBomMappingsQuery({
    mapping_type: ReleasesType.bomMappingType.Part,
  });

  return (
    <Flex flexDirection="column" gapY={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading fontSize="sm">Part BOM Mappings</Heading>

        <PermissionCheck permissions={[permissionsKeys.add_release]}>
          <Button size="sm" onClick={handleCreateBomMapping}>
            New
          </Button>
        </PermissionCheck>
      </Flex>

      {!bomMappings?.length && (
        <Card>
          <Flex flexDirection="column" gapY={4}>
            <Text fontSize="sm" color="fg.subtle">
              Part BOM mappings will render here once saved.
            </Text>
          </Flex>
        </Card>
      )}

      {bomMappings?.map((bomMapping, index) => (
        <Card key={`release-bom-mapping-${bomMapping?.id}-${index}`}>
          <Flex flexDirection="column" gapY={4}>
            <Flex flexDirection="column">
              <Flex justifyContent="space-between" alignItems="center">
                <Flex gapX={2} alignItems="center" flexWrap="wrap">
                  <Text>{bomMapping?.name}</Text>
                  <Badge bg="green.200" color="green" height="fit-content">
                    <Text>Active</Text>
                  </Badge>
                  <Badge bg="border.emphasized" color="fg" height="fit-content">
                    <Text>Sheet: {bomMapping?.sheet_name || 'BOM'}</Text>
                  </Badge>
                  <Badge bg="border.emphasized" color="fg" height="fit-content">
                    <Text>
                      Header Row: {(bomMapping?.header_row_index ?? 0) + 1}
                    </Text>
                  </Badge>
                </Flex>
              </Flex>

              <Flex fontSize="sm" color="fg.subtle" flexDirection="column">
                <Text>File mapping: {bomMapping?.name}</Text>
              </Flex>
            </Flex>

            <Flex gapX={2} justifyContent="end" flexWrap="wrap">
              <Button
                size="sm"
                variant="outline"
                color="fg"
                borderColor="fg"
                onClick={() => handleViewBomMapping(bomMapping as BomMappingForm)}
              >
                View
              </Button>

              <PermissionCheck permissions={[permissionsKeys.change_release]}>
                <Button
                  size="sm"
                  variant="outline"
                  color="fg"
                  borderColor="fg"
                  onClick={() => handleEditBomMapping(bomMapping as BomMappingForm)}
                >
                  Edit
                </Button>
              </PermissionCheck>

              <PermissionCheck permissions={[permissionsKeys.change_release]}>
                <Button
                  size="sm"
                  variant="outline"
                  color="red.300"
                  borderColor="red.300"
                  onClick={() =>
                    handleDeleteBomMapping(bomMapping as BomMappingForm)
                  }
                >
                  Delete
                </Button>
              </PermissionCheck>

             
            </Flex>
          </Flex>
        </Card>
      ))}

      <Card>
        <Text fontSize="sm" color="fg.subtle">
          New mappings will appear here after saving.
        </Text>
      </Card>
    </Flex>
  );
};

const CreateBomMappingOverlayDialogue = ({
  overlayId,
}: {
  overlayId: string;
}) => {
  const [bomFile, setBomFile] = useState<File | undefined>(undefined);
  const [inspectError, setInspectError] = useState<string | null>(null);
  const [isInspecting, setIsInspecting] = useState(false);

  const [workbookInfo, setWorkbookInfo] =
    useState<WorkbookInspectResponse | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState<string>('');
  const [selectedHeaderRowIndex, setSelectedHeaderRowIndex] =
    useState<number>(0);

  const [postBomMappings] = ReleasesApi.Api.usePostBomMappingsMutation();
  const [postBomMappingGetHeaders] =
    ReleasesApi.Api.usePostBomMappingGetHeadersMutation();
  const [postAttachmentsPresignedUrls] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const [selectedPartNumber, setSelectedPartNumber] = useState<string | null>(
    null,
  );
  const [selectedTotalQuantity, setSelectedTotalQuantity] = useState<
    string | null
  >(null);

  const [bomMappingFormData, setBomMappingFormData] =
    useState<BomMappingForm | null>(null);

  const [bomMappingErrors, setBomMappingErrors] = useState<
    Partial<Record<string, string>>
  >({});

  const selectedSheet = useMemo(
    () => workbookInfo?.sheets?.find((s) => s.name === selectedSheetName) ?? null,
    [workbookInfo, selectedSheetName],
  );

  const bomFileHeaders = selectedSheet?.headers ?? [];

  const bomFileHeadersAvailableForSelectionForTotalQuantity = bomFileHeaders.filter(
    (header) => header !== selectedPartNumber,
  );

  useEffect(() => {
    if (!bomFile) {
      setWorkbookInfo(null);
      setSelectedSheetName('');
      setSelectedHeaderRowIndex(0);
      setInspectError(null);
      return;
    }

    const inspectWorkbookFromS3 = async () => {
      try {
        setIsInspecting(true);
        setInspectError(null);

        const uploadFilename = buildUploadFilename(bomFile);

        const presignedUrls = await postAttachmentsPresignedUrls({
          body: [
            {
              module: 'bom',
              filename: uploadFilename,
              content_type: bomFile.type || 'application/octet-stream',
              size: bomFile.size,
            },
          ],
        }).unwrap();

        const presigned = presignedUrls?.find(
          (item) => item.filename === uploadFilename,
        );

        if (!presigned) {
          throw new Error('Failed to get presigned upload URL for BOM mapping file');
        }

        const fileUrl = await uploadFileToPresignedUrl(bomFile, presigned);

        const payload = (await postBomMappingGetHeaders({
          file_url: fileUrl,
        }).unwrap()) as WorkbookInspectResponse;

        setWorkbookInfo(payload);

        const recommendedSheetName =
          payload.recommended_sheet_name || payload.sheets?.[0]?.name || '';
        const recommendedSheet = payload.sheets?.find(
          (s) => s.name === recommendedSheetName,
        );

        setSelectedSheetName(recommendedSheetName);
        setSelectedHeaderRowIndex(
          recommendedSheet?.detected_header_row_index ?? 0,
        );
      } catch (err: any) {
        setInspectError(
          err?.data?.detail || err?.message || 'Failed to inspect workbook',
        );
        setWorkbookInfo(null);
      } finally {
        setIsInspecting(false);
      }
    };

    inspectWorkbookFromS3();
  }, [bomFile, postAttachmentsPresignedUrls, postBomMappingGetHeaders]);

  useEffect(() => {
    const headers = selectedSheet?.headers ?? [];
    if (!headers.length) return;

    setBomMappingFormData((prev) => ({
      ...(prev ?? {
        name: '',
        mapping: DEFAULT_MAPPING,
        created_by: 0,
        sheet_name: selectedSheetName,
        header_row_index: selectedHeaderRowIndex,
      }),
      sheet_name: selectedSheetName,
      header_row_index: selectedHeaderRowIndex,
      mapping: rebuildMappingFromHeaders(
        headers,
        selectedPartNumber,
        selectedTotalQuantity,
        prev?.mapping,
      ),
    }));
  }, [
    selectedSheetName,
    selectedHeaderRowIndex,
    selectedSheet,
    selectedPartNumber,
    selectedTotalQuantity,
  ]);

  const updateBomMappingField = <
    K extends keyof ReleasesType.BOMMappings['mapping'][string],
  >(
    key: string,
    field: K,
    value: ReleasesType.BOMMappings['mapping'][string][K],
  ) => {
    setBomMappingFormData((prev) => {
      const base = prev ?? {
        name: '',
        mapping: DEFAULT_MAPPING,
        created_by: 0,
        sheet_name: selectedSheetName,
        header_row_index: selectedHeaderRowIndex,
      };

      const normalizedMapping = ensureMappingDefaults(base.mapping);

      return {
        ...base,
        sheet_name: selectedSheetName,
        header_row_index: selectedHeaderRowIndex,
        mapping: {
          ...normalizedMapping,
          [key]: {
            ...(normalizedMapping[key] ?? { source: '' }),
            [field]: value,
          },
        },
      };
    });
  };

  const handleSubmitBomMappings = async () => {
    const safeParseForm = newBomMappingRequiredFieldsSchema.safeParse(
      bomMappingFormData ?? {
        name: '',
        sheet_name: '',
        header_row_index: 0,
        mapping: DEFAULT_MAPPING,
      },
    );

    if (!safeParseForm.success) {
      const fieldErrors: Partial<Record<string, string>> = {};
      safeParseForm.error?.issues.forEach((err) => {
        const key = String(err.path[0] ?? 'form');
        fieldErrors[key] = err.message;
      });
      setBomMappingErrors(fieldErrors);
      return;
    }

    try {
      if (!bomMappingFormData) return;

      await postBomMappings({
        ...bomMappingFormData,
        mapping: ensureMappingDefaults(bomMappingFormData.mapping),
      }).unwrap();

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'BOM Mapping created successfully',
      });

      OverlayDialog.close(overlayId);
      setBomMappingFormData(null);
    } catch (err: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message:
          err?.data?.detail ||
          err?.data?.message ||
          'Could not create BOM Mapping',
      });
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Flex justifyContent="space-between">
            <Flex gapX={2}>
              <Box
                height={10}
                width={10}
                bg="bg.emphasized"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <FiUploadCloud />
              </Box>
              <Flex flexDirection="column">
                <Text>
                  Upload Excel (.xlsx, .xls) <Span color="fg.error">*</Span>
                </Text>
                <Text fontSize="xs" color="fg.subtle">
                  File is uploaded to S3 first, then inspected from S3.
                </Text>
              </Flex>
            </Flex>

            {bomFile && (
              <Box maxWidth={240}>
                <Badge color="fg" bg="border.emphasized" height="fit-content">
                  <Text whiteSpace="wrap" wordBreak="break-word">
                    {bomFile.name}
                  </Text>
                </Badge>
              </Box>
            )}
          </Flex>

          <Box>
            <FileUpload
              fileType="excel"
              maxFiles={1}
              inputProps={{ 'data-testid': 'tutorial-part-bom-upload' }}
              onUpload={(file) => {
                if (Array.isArray(file)) {
                  setBomFile(file[0]);
                } else {
                  setBomFile(file);
                }
              }}
            />
          </Box>
        </Flex>
      </Card>

      {isInspecting && <Spinner isLoading text="Inspecting workbook..." />}

      {inspectError && (
        <Alert status="error">{`Error inspecting workbook: ${inspectError}`}</Alert>
      )}

      {!!workbookInfo?.sheets?.length && (
        <Card>
          <Flex flexDirection="column" gapY={4}>
            <Text fontSize="sm" fontWeight="bold">
              Workbook Selection
            </Text>

            <Flex gap={4} flexWrap="wrap">
              <Flex flexDirection="column" minW="260px" flex={1}>
                <Text fontSize="sm">
                  Sheet <Span color="fg.error">*</Span>
                </Text>
                <Box width="100%" minW="260px">
                  <SelectWithAction
                    items={workbookInfo.sheets.map((s) => s.name)}
                    placeholder="Choose sheet"
                    value={selectedSheetName}
                    onSelect={(value: string) => {
                      setSelectedSheetName(value);
                      const nextSheet = workbookInfo.sheets.find(
                        (s) => s.name === value,
                      );
                      setSelectedHeaderRowIndex(
                        nextSheet?.detected_header_row_index ?? 0,
                      );
                      setSelectedPartNumber(null);
                      setSelectedTotalQuantity(null);
                    }}
                  />
                </Box>
              </Flex>

              <Flex flexDirection="column" minW="320px" flex={1.4}>
                <Text fontSize="sm">
                  Header Row <Span color="fg.error">*</Span>
                </Text>
                <Box width="100%" minW="320px">
                  <SelectWithAction
                    items={(selectedSheet?.header_candidates ?? []).map((c) =>
                      formatCandidateLabel(c),
                    )}
                    placeholder="Choose header row"
                    value={
                      selectedSheet?.header_candidates?.find(
                        (c) => c.row_index === selectedHeaderRowIndex,
                      )
                        ? formatCandidateLabel(
                            selectedSheet.header_candidates.find(
                              (c) => c.row_index === selectedHeaderRowIndex,
                            )!,
                          )
                        : undefined
                    }
                    onSelect={(value: string) => {
                      const candidate = (selectedSheet?.header_candidates ?? []).find(
                        (c) => formatCandidateLabel(c) === value,
                      );
                      if (!candidate) return;
                      setSelectedHeaderRowIndex(candidate.row_index);
                      setSelectedPartNumber(null);
                      setSelectedTotalQuantity(null);
                    }}
                  />
                </Box>
              </Flex>
            </Flex>

            <Flex gap={2} flexWrap="wrap">
              <Badge bg="border.emphasized" color="fg">
                <Text>
                  Recommended Sheet: {workbookInfo.recommended_sheet_name}
                </Text>
              </Badge>
              <Badge bg="border.emphasized" color="fg">
                <Text>Selected Header Row: {selectedHeaderRowIndex + 1}</Text>
              </Badge>
            </Flex>
          </Flex>
        </Card>
      )}

      {!!workbookInfo?.sheets?.length && (
        <>
          <Card>
            <Flex flexDirection="column" gapY={4}>
              <Text fontSize="sm" fontWeight="bold">
                Mapping name <Span color="fg.error">*</Span>
              </Text>

              <Input
                label=""
                minWidth="full"
                onChange={(inputValue: string) => {
                  setBomMappingErrors((prev) => ({ ...prev, name: undefined }));
                  setBomMappingFormData((prev) =>
                    prev
                      ? { ...prev, name: inputValue }
                      : {
                          name: inputValue,
                          mapping: DEFAULT_MAPPING,
                          created_by: 0,
                          sheet_name: selectedSheetName,
                          header_row_index: selectedHeaderRowIndex,
                        },
                  );
                }}
              />
            </Flex>
          </Card>

          <Flex gap={6} flexWrap="wrap" alignItems="flex-end">
            <Flex flexDirection="column" minW="280px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Part number Column <Span color="fg.error">*</Span>
              </Text>
              <Box width="100%" minW="280px">
                <SelectWithAction
                  items={bomFileHeaders}
                  value={selectedPartNumber ?? undefined}
                  onSelect={(value: string) => {
                    setBomMappingErrors((prev) => ({
                      ...prev,
                      part_number: undefined,
                    }));
                    setSelectedPartNumber(value);
                    updateBomMappingField('part_number', 'source', value);
                    updateBomMappingField(
                      'part_number',
                      'data_type',
                      ReleasesType.itemType.String,
                    );
                  }}
                  placeholder="Choose an option"
                />
              </Box>
            </Flex>

            <Flex flexDirection="column" minW="280px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Total Qty Column <Span color="fg.error">*</Span>
              </Text>
              <Box width="100%" minW="280px">
                <SelectWithAction
                  items={bomFileHeadersAvailableForSelectionForTotalQuantity}
                  value={selectedTotalQuantity ?? undefined}
                  onSelect={(value: string) => {
                    setBomMappingErrors((prev) => ({
                      ...prev,
                      total_part_qty: undefined,
                    }));
                    setSelectedTotalQuantity(value);
                    updateBomMappingField('total_part_qty', 'source', value);
                    updateBomMappingField(
                      'total_part_qty',
                      'data_type',
                      ReleasesType.itemType.Integer,
                    );
                  }}
                  placeholder="Choose an option"
                  disabled={!selectedPartNumber}
                />
              </Box>
            </Flex>
          </Flex>

          <BomMappingTable
            mapping={ensureMappingDefaults(bomMappingFormData?.mapping)}
            onUpdateField={updateBomMappingField}
          />
        </>
      )}

      <Separator />
      <Box>
        <Button
          size="sm"
          type="submit"
          disabled={!workbookInfo?.sheets?.length}
          onClick={handleSubmitBomMappings}
          data-testid="tutorial-bom-map-confirm"
        >
          Save Mapping
        </Button>
      </Box>
    </Flex>
  );
};

const ViewBomMappingOverlayDialogue = ({
  overlayId,
  bomMapping,
}: {
  overlayId: string;
  bomMapping: BomMappingForm;
}) => {
  const normalizedMapping = useMemo(
    () => ensureMappingDefaults(bomMapping.mapping),
    [bomMapping.mapping],
  );

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Text fontSize="sm" fontWeight="bold">
            Mapping name
          </Text>
          <Input label="" minWidth="full" value={bomMapping.name || ''} disabled />

          <Flex gap={6} flexWrap="wrap" alignItems="flex-end">
            <Flex flexDirection="column" minW="280px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Part number Column
              </Text>
              <Box width="100%" minW="280px">
                <Input
                  label=""
                  minWidth="full"
                  value={normalizedMapping.part_number?.source || ''}
                  disabled
                />
              </Box>
            </Flex>

            <Flex flexDirection="column" minW="280px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Total Qty Column
              </Text>
              <Box width="100%" minW="280px">
                <Input
                  label=""
                  minWidth="full"
                  value={normalizedMapping.total_part_qty?.source || ''}
                  disabled
                />
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Text fontSize="sm" fontWeight="bold">
            Workbook Selection
          </Text>

          <Flex gap={2} flexWrap="wrap">
            <Badge bg="border.emphasized" color="fg">
              <Text>Sheet: {bomMapping.sheet_name || '-'}</Text>
            </Badge>
            <Badge bg="border.emphasized" color="fg">
              <Text>Header Row: {(bomMapping.header_row_index ?? 0) + 1}</Text>
            </Badge>
          </Flex>
        </Flex>
      </Card>

      <BomMappingTable mapping={normalizedMapping} readOnly />

      <Flex justifyContent="flex-end">
        <Button onClick={() => OverlayDialog.close(overlayId)}>Close</Button>
      </Flex>
    </Flex>
  );
};

const EditBomMappingOverlayDialogue = ({
  overlayId,
  bomMapping,
}: {
  overlayId: string;
  bomMapping: BomMappingForm;
}) => {
  const [formData, setFormData] = useState<BomMappingForm>({
    ...bomMapping,
    mapping: ensureMappingDefaults(bomMapping.mapping),
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<string, string>>>(
    {},
  );

  const [patchBomMapping] = ReleasesApi.Api.usePatchBomMappingMutation();

  const normalizedMapping = useMemo(
    () => ensureMappingDefaults(formData.mapping),
    [formData.mapping],
  );

  const updateTopLevelField = <K extends keyof BomMappingForm>(
    key: K,
    value: BomMappingForm[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateMappingField = <
    K extends keyof ReleasesType.BOMMappings['mapping'][string],
  >(
    key: string,
    field: K,
    value: ReleasesType.BOMMappings['mapping'][string][K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      mapping: {
        ...ensureMappingDefaults(prev.mapping),
        [key]: {
          ...(ensureMappingDefaults(prev.mapping)[key] ?? { source: '' }),
          [field]: value,
        },
      },
    }));
  };

  const handleSubmit = async () => {
    const safeParseForm = newBomMappingRequiredFieldsSchema.safeParse({
      name: formData.name,
      sheet_name: formData.sheet_name,
      header_row_index: formData.header_row_index,
      mapping: normalizedMapping,
    });

    if (!safeParseForm.success) {
      const fieldErrors: Partial<Record<string, string>> = {};
      safeParseForm.error.issues.forEach((err) => {
        const key = String(err.path[0] ?? 'form');
        fieldErrors[key] = err.message;
      });
      setFormErrors(fieldErrors);
      return;
    }

    try {
      if (!formData.id) {
        toasterApi({
          type: 'error',
          status: 400,
          code: 'Error',
          message: 'Missing BOM mapping id',
        });
        return;
      }

      await patchBomMapping({
        id: formData.id,
        body: {
          name: formData.name,
          mapping: normalizedMapping,
          sheet_name: formData.sheet_name,
          header_row_index: formData.header_row_index,
        },
      }).unwrap();

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'BOM Mapping updated successfully',
      });

      OverlayDialog.close(overlayId);
    } catch (err: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message:
          err?.data?.detail ||
          err?.data?.message ||
          'Could not update BOM Mapping',
      });
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Text fontSize="sm" fontWeight="bold">
            Mapping name <Span color="fg.error">*</Span>
          </Text>

          <Input
            label=""
            minWidth="full"
            value={formData.name}
            onChange={(value: string) => {
              setFormErrors((prev) => ({ ...prev, name: undefined }));
              updateTopLevelField('name', value);
            }}
          />

          <Flex gap={6} flexWrap="wrap" alignItems="flex-end">
            <Flex flexDirection="column" minW="280px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Part number Column
              </Text>
              <Box width="100%" minW="280px">
                <Input
                  label=""
                  minWidth="full"
                  value={normalizedMapping.part_number?.source || ''}
                  disabled
                />
              </Box>
            </Flex>

            <Flex flexDirection="column" minW="280px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Total Qty Column
              </Text>
              <Box width="100%" minW="280px">
                <Input
                  label=""
                  minWidth="full"
                  value={normalizedMapping.total_part_qty?.source || ''}
                  disabled
                />
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Text fontSize="sm" fontWeight="bold">
            Workbook Selection
          </Text>

          <Flex gap={2} flexWrap="wrap">
            <Badge bg="border.emphasized" color="fg">
              <Text>Sheet: {formData.sheet_name || '-'}</Text>
            </Badge>
            <Badge bg="border.emphasized" color="fg">
              <Text>Header Row: {(formData.header_row_index ?? 0) + 1}</Text>
            </Badge>
          </Flex>
        </Flex>
      </Card>

      <BomMappingTable
        mapping={normalizedMapping}
        onUpdateField={updateMappingField}
      />

      {!!formErrors.form && <Alert status="error">{formErrors.form}</Alert>}

      <Flex justifyContent="flex-end" gap={2}>
        <Button variant="outline" onClick={() => OverlayDialog.close(overlayId)}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Save Changes</Button>
      </Flex>
    </Flex>
  );
};

const DeleteBomMappingOverlayDialogue = ({
  overlayId,
  bomMapping,
}: {
  overlayId: string;
  bomMapping: BomMappingForm;
}) => {
  const [deleteBomMapping, { isLoading }] =
    ReleasesApi.Api.useDeleteBomMappingMutation();

  const handleDelete = async () => {
    try {
      if (!bomMapping.id) {
        toasterApi({
          type: 'error',
          status: 400,
          code: 'Error',
          message: 'Missing BOM mapping id',
        });
        return;
      }

      await deleteBomMapping({ id: bomMapping.id }).unwrap();

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'BOM Mapping deleted successfully',
      });

      OverlayDialog.close(overlayId);
    } catch (err: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message:
          err?.data?.detail ||
          err?.data?.message ||
          'Could not delete BOM Mapping',
      });
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={3}>
          <Text>This will permanently delete the BOM mapping:</Text>
          <Text fontWeight="bold">{bomMapping.name}</Text>
        </Flex>
      </Card>

      <Flex justifyContent="flex-end" gap={2}>
        <Button
          variant="outline"
          onClick={() => OverlayDialog.close(overlayId)}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          colorPalette="red"
          onClick={handleDelete}
          disabled={isLoading}
        >
          {isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </Flex>
    </Flex>
  );
};

const newBomMappingRequiredFieldsSchema = z.object({
  name: z.string().nonempty({ error: 'Mapping name is required' }),
  sheet_name: z.string().nonempty({ error: 'Sheet selection is required' }),
  header_row_index: z.number().min(0),
  mapping: z.object({
    part_number: z.object({
      source: z
        .string()
        .nonempty({ error: 'Part number column selection is required' }),
    }),
    total_part_qty: z.object({
      source: z
        .string()
        .nonempty({ error: 'Total quantity column selection is required' }),
      data_type: z.literal(ReleasesType.itemType.Integer),
    }),
  }),
});

// ---------------------------------------------------------------------------
// Frame BOM Mappings
// ---------------------------------------------------------------------------

// Optional frame attributes that can be mapped to source columns. Frame number
// and quantity are required and handled separately.
const OPTIONAL_FRAME_ATTRIBUTES: { key: string; label: string }[] = [
  { key: 'description', label: 'Description' },
  { key: 'type', label: 'Type' },
  { key: 'system', label: 'System' },
  { key: 'dimensions', label: 'Dimensions' },
  { key: 'notes', label: 'Notes' },
];

const FRAME_NONE_OPTION = '— None —';

const frameBomMappingRequiredFieldsSchema = z.object({
  name: z.string().nonempty({ error: 'Mapping name is required' }),
  sheet_name: z.string().nonempty({ error: 'Sheet selection is required' }),
  header_row_index: z.number().min(0),
  mapping: z.object({
    frame_number: z.object({
      source: z
        .string()
        .nonempty({ error: 'Frame number column selection is required' }),
    }),
    quantity: z.object({
      source: z
        .string()
        .nonempty({ error: 'Quantity column selection is required' }),
      data_type: z.literal(ReleasesType.itemType.Integer),
    }),
  }),
});

const FrameBomMappingList = () => {
  const { data: frameMappings } = ReleasesApi.Api.useGetBomMappingsQuery({
    mapping_type: ReleasesType.bomMappingType.Frame,
  });

  const handleCreate = () => {
    OverlayDialog.open('createFrameBomMapping', {
      overlayId: 'createFrameBomMapping',
      title: 'New Frame BOM Mapping',
      description:
        'Upload a Frame BOM (frame quantity) file, choose the sheet/header row, then map the frame number and quantity columns.',
      content: (
        <CreateFrameBomMappingOverlayDialogue overlayId="createFrameBomMapping" />
      ),
    });
  };

  const handleView = (bomMapping: BomMappingForm) => {
    OverlayDialog.open(`viewFrameBomMapping-${bomMapping.id}`, {
      overlayId: `viewFrameBomMapping-${bomMapping.id}`,
      title: 'View Frame BOM Mapping',
      description: bomMapping.name,
      content: (
        <ViewFrameBomMappingOverlayDialogue
          overlayId={`viewFrameBomMapping-${bomMapping.id}`}
          bomMapping={bomMapping}
        />
      ),
    });
  };

  const handleEdit = (bomMapping: BomMappingForm) => {
    OverlayDialog.open(`editFrameBomMapping-${bomMapping.id}`, {
      overlayId: `editFrameBomMapping-${bomMapping.id}`,
      title: 'Edit Frame BOM Mapping',
      description: bomMapping.name,
      content: (
        <EditFrameBomMappingOverlayDialogue
          overlayId={`editFrameBomMapping-${bomMapping.id}`}
          bomMapping={bomMapping}
        />
      ),
    });
  };

  const handleDelete = (bomMapping: BomMappingForm) => {
    OverlayDialog.open(`deleteFrameBomMapping-${bomMapping.id}`, {
      overlayId: `deleteFrameBomMapping-${bomMapping.id}`,
      title: 'Delete Frame BOM Mapping',
      description: `Are you sure you want to delete "${bomMapping.name}"?`,
      content: (
        <DeleteBomMappingOverlayDialogue
          overlayId={`deleteFrameBomMapping-${bomMapping.id}`}
          bomMapping={bomMapping}
        />
      ),
    });
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading fontSize="sm">Frame BOM Mappings</Heading>

        <PermissionCheck permissions={[permissionsKeys.add_release]}>
          <Button size="sm" onClick={handleCreate}>
            New
          </Button>
        </PermissionCheck>
      </Flex>

      {!frameMappings?.length && (
        <Card>
          <Flex flexDirection="column" gapY={2}>
            <Text fontSize="sm" color="fg.subtle">
              No Frame BOM mappings yet. Create one to import frame quantities
              when uploading a release.
            </Text>
          </Flex>
        </Card>
      )}

      {frameMappings?.map((bomMapping, index) => (
        <Card key={`release-frame-bom-mapping-${bomMapping?.id}-${index}`}>
          <Flex flexDirection="column" gapY={4}>
            <Flex flexDirection="column">
              <Flex gapX={2} alignItems="center" flexWrap="wrap">
                <Text>{bomMapping?.name}</Text>
                <Badge bg="green.200" color="green" height="fit-content">
                  <Text>Active</Text>
                </Badge>
                <Badge bg="border.emphasized" color="fg" height="fit-content">
                  <Text>Sheet: {bomMapping?.sheet_name || '-'}</Text>
                </Badge>
                <Badge bg="border.emphasized" color="fg" height="fit-content">
                  <Text>
                    Header Row: {(bomMapping?.header_row_index ?? 0) + 1}
                  </Text>
                </Badge>
              </Flex>
            </Flex>

            <Flex gapX={2} justifyContent="end" flexWrap="wrap">
              <Button
                size="sm"
                variant="outline"
                color="fg"
                borderColor="fg"
                onClick={() => handleView(bomMapping as BomMappingForm)}
              >
                View
              </Button>

              <PermissionCheck permissions={[permissionsKeys.change_release]}>
                <Button
                  size="sm"
                  variant="outline"
                  color="fg"
                  borderColor="fg"
                  onClick={() => handleEdit(bomMapping as BomMappingForm)}
                >
                  Edit
                </Button>
              </PermissionCheck>

              <PermissionCheck permissions={[permissionsKeys.change_release]}>
                <Button
                  size="sm"
                  variant="outline"
                  color="red.300"
                  borderColor="red.300"
                  onClick={() => handleDelete(bomMapping as BomMappingForm)}
                >
                  Delete
                </Button>
              </PermissionCheck>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
};

const CreateFrameBomMappingOverlayDialogue = ({
  overlayId,
}: {
  overlayId: string;
}) => {
  const [frameFile, setFrameFile] = useState<File | undefined>(undefined);
  const [inspectError, setInspectError] = useState<string | null>(null);
  const [isInspecting, setIsInspecting] = useState(false);

  const [workbookInfo, setWorkbookInfo] =
    useState<WorkbookInspectResponse | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState<string>('');
  const [selectedHeaderRowIndex, setSelectedHeaderRowIndex] =
    useState<number>(0);

  const [name, setName] = useState('');
  const [frameNumberCol, setFrameNumberCol] = useState<string | null>(null);
  const [quantityCol, setQuantityCol] = useState<string | null>(null);
  const [attributeCols, setAttributeCols] = useState<Record<string, string>>(
    {},
  );
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const [postBomMappings] = ReleasesApi.Api.usePostBomMappingsMutation();
  const [postBomMappingGetHeaders] =
    ReleasesApi.Api.usePostBomMappingGetHeadersMutation();
  const [postAttachmentsPresignedUrls] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const selectedSheet = useMemo(
    () => workbookInfo?.sheets?.find((s) => s.name === selectedSheetName) ?? null,
    [workbookInfo, selectedSheetName],
  );

  const headers = selectedSheet?.headers ?? [];
  const quantityOptions = headers.filter((h) => h !== frameNumberCol);

  useEffect(() => {
    if (!frameFile) {
      setWorkbookInfo(null);
      setSelectedSheetName('');
      setSelectedHeaderRowIndex(0);
      setFrameNumberCol(null);
      setQuantityCol(null);
      setAttributeCols({});
      setInspectError(null);
      return;
    }

    const inspectWorkbookFromS3 = async () => {
      try {
        setIsInspecting(true);
        setInspectError(null);

        const uploadFilename = buildUploadFilename(frameFile);

        const presignedUrls = await postAttachmentsPresignedUrls({
          body: [
            {
              module: 'frame_qty',
              filename: uploadFilename,
              content_type: frameFile.type || 'application/octet-stream',
              size: frameFile.size,
            },
          ],
        }).unwrap();

        const presigned = presignedUrls?.find(
          (item) => item.filename === uploadFilename,
        );

        if (!presigned) {
          throw new Error(
            'Failed to get presigned upload URL for frame BOM file',
          );
        }

        const fileUrl = await uploadFileToPresignedUrl(frameFile, presigned);

        const payload = (await postBomMappingGetHeaders({
          file_url: fileUrl,
          module: 'frame_qty',
        }).unwrap()) as WorkbookInspectResponse;

        setWorkbookInfo(payload);

        const recommendedSheetName =
          payload.recommended_sheet_name || payload.sheets?.[0]?.name || '';
        const recommendedSheet = payload.sheets?.find(
          (s) => s.name === recommendedSheetName,
        );

        setSelectedSheetName(recommendedSheetName);
        setSelectedHeaderRowIndex(
          recommendedSheet?.detected_header_row_index ?? 0,
        );
      } catch (err: any) {
        setInspectError(
          err?.data?.detail || err?.message || 'Failed to inspect workbook',
        );
        setWorkbookInfo(null);
      } finally {
        setIsInspecting(false);
      }
    };

    inspectWorkbookFromS3();
  }, [frameFile, postAttachmentsPresignedUrls, postBomMappingGetHeaders]);

  const handleSubmit = async () => {
    const attributes: Record<string, { source: string }> = {};
    Object.entries(attributeCols).forEach(([key, col]) => {
      if (col) attributes[key] = { source: col };
    });

    const mapping: ReleasesType.BOMMappings['mapping'] = {
      frame_number: { source: frameNumberCol || '' },
      quantity: {
        source: quantityCol || '',
        data_type: ReleasesType.itemType.Integer,
      },
      ...attributes,
    };

    const parsed = frameBomMappingRequiredFieldsSchema.safeParse({
      name,
      sheet_name: selectedSheetName,
      header_row_index: selectedHeaderRowIndex,
      mapping,
    });

    if (!parsed.success) {
      const fieldErrors: Partial<Record<string, string>> = {};
      parsed.error.issues.forEach((err) => {
        const key = String(err.path[err.path.length - 1] ?? 'form');
        fieldErrors[key] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    try {
      await postBomMappings({
        name,
        mapping_type: ReleasesType.bomMappingType.Frame,
        mapping,
        sheet_name: selectedSheetName,
        header_row_index: selectedHeaderRowIndex,
        created_by: 0,
      }).unwrap();

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'Frame BOM Mapping created successfully',
      });

      OverlayDialog.close(overlayId);
    } catch (err: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message:
          err?.data?.detail ||
          err?.data?.message ||
          'Could not create Frame BOM Mapping',
      });
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Flex gapX={2}>
            <Box
              height={10}
              width={10}
              bg="bg.emphasized"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <FiUploadCloud />
            </Box>
            <Flex flexDirection="column">
              <Text>
                Upload Frame BOM Excel (.xlsx, .xls){' '}
                <Span color="fg.error">*</Span>
              </Text>
              <Text fontSize="xs" color="fg.subtle">
                File is uploaded to S3 first, then inspected from S3.
              </Text>
            </Flex>
          </Flex>

          {frameFile && (
            <Box maxWidth={320}>
              <Badge color="fg" bg="border.emphasized" height="fit-content">
                <Text whiteSpace="wrap" wordBreak="break-word">
                  {frameFile.name}
                </Text>
              </Badge>
            </Box>
          )}

          <Box>
            <FileUpload
              fileType="excel"
              maxFiles={1}
              inputProps={{ 'data-testid': 'tutorial-frame-bom-upload' }}
              onUpload={(file: File | File[]) => {
                if (Array.isArray(file)) {
                  setFrameFile(file[0]);
                } else {
                  setFrameFile(file);
                }
              }}
            />
          </Box>
        </Flex>
      </Card>

      {isInspecting && (
        <Spinner isLoading text="Inspecting frame workbook..." />
      )}

      {inspectError && (
        <Alert status="error">{`Error inspecting workbook: ${inspectError}`}</Alert>
      )}

      {!!workbookInfo?.sheets?.length && (
        <Card>
          <Flex flexDirection="column" gapY={4}>
            <Text fontSize="sm" fontWeight="bold">
              Workbook Selection
            </Text>

            <Flex gap={4} flexWrap="wrap">
              <Flex flexDirection="column" minW="240px" flex={1}>
                <Text fontSize="sm">
                  Sheet <Span color="fg.error">*</Span>
                </Text>
                <Box width="100%" minW="240px">
                  <SelectWithAction
                    items={workbookInfo.sheets.map((s) => s.name)}
                    placeholder="Choose sheet"
                    value={selectedSheetName}
                    onSelect={(value: string) => {
                      setSelectedSheetName(value);
                      const nextSheet = workbookInfo.sheets.find(
                        (s) => s.name === value,
                      );
                      setSelectedHeaderRowIndex(
                        nextSheet?.detected_header_row_index ?? 0,
                      );
                      setFrameNumberCol(null);
                      setQuantityCol(null);
                      setAttributeCols({});
                    }}
                  />
                </Box>
              </Flex>

              <Flex flexDirection="column" minW="320px" flex={1.4}>
                <Text fontSize="sm">
                  Header Row <Span color="fg.error">*</Span>
                </Text>
                <Box width="100%" minW="320px">
                  <SelectWithAction
                    items={(selectedSheet?.header_candidates ?? []).map((c) =>
                      formatCandidateLabel(c),
                    )}
                    placeholder="Choose header row"
                    value={
                      selectedSheet?.header_candidates?.find(
                        (c) => c.row_index === selectedHeaderRowIndex,
                      )
                        ? formatCandidateLabel(
                            selectedSheet.header_candidates.find(
                              (c) => c.row_index === selectedHeaderRowIndex,
                            )!,
                          )
                        : undefined
                    }
                    onSelect={(value: string) => {
                      const candidate = (
                        selectedSheet?.header_candidates ?? []
                      ).find((c) => formatCandidateLabel(c) === value);
                      if (!candidate) return;
                      setSelectedHeaderRowIndex(candidate.row_index);
                      setFrameNumberCol(null);
                      setQuantityCol(null);
                      setAttributeCols({});
                    }}
                  />
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      )}

      {!!workbookInfo?.sheets?.length && (
        <>
          <Card>
            <Flex flexDirection="column" gapY={4}>
              <Text fontSize="sm" fontWeight="bold">
                Mapping name <Span color="fg.error">*</Span>
              </Text>
              <Input
                label=""
                minWidth="full"
                value={name}
                onChange={(value: string) => {
                  setErrors((prev) => ({ ...prev, name: undefined }));
                  setName(value);
                }}
              />
              {errors.name && <Alert status="error">{errors.name}</Alert>}
            </Flex>
          </Card>

          <Card>
            <Flex flexDirection="column" gapY={4}>
              <Text fontSize="sm" fontWeight="bold">
                Required Columns
              </Text>

              <Flex gap={6} flexWrap="wrap" alignItems="flex-end">
                <Flex flexDirection="column" minW="260px" flex={1}>
                  <Text whiteSpace="nowrap" mb={2}>
                    Frame Number / Mark <Span color="fg.error">*</Span>
                  </Text>
                  <Box width="100%" minW="260px">
                    <SelectWithAction
                      data-testid="tutorial-frame-number-column-select"
                      items={headers}
                      value={frameNumberCol ?? undefined}
                      onSelect={(value: string) => {
                        setErrors((prev) => ({
                          ...prev,
                          frame_number: undefined,
                        }));
                        setFrameNumberCol(value);
                        if (value === quantityCol) setQuantityCol(null);
                      }}
                      placeholder="Choose column"
                    />
                  </Box>
                  {errors.frame_number && (
                    <Text fontSize="xs" color="fg.error">
                      {errors.frame_number}
                    </Text>
                  )}
                </Flex>

                <Flex flexDirection="column" minW="260px" flex={1}>
                  <Text whiteSpace="nowrap" mb={2}>
                    Quantity <Span color="fg.error">*</Span>
                  </Text>
                  <Box width="100%" minW="260px">
                    <SelectWithAction
                      data-testid="tutorial-frame-quantity-column-select"
                      items={quantityOptions}
                      value={quantityCol ?? undefined}
                      onSelect={(value: string) => {
                        setErrors((prev) => ({ ...prev, quantity: undefined }));
                        setQuantityCol(value);
                      }}
                      placeholder="Choose column"
                      disabled={!frameNumberCol}
                    />
                  </Box>
                  {errors.quantity && (
                    <Text fontSize="xs" color="fg.error">
                      {errors.quantity}
                    </Text>
                  )}
                </Flex>
              </Flex>

              <Separator />

              <Text fontSize="sm" fontWeight="bold">
                Optional Attributes
              </Text>
              <Text fontSize="xs" color="fg.subtle">
                Map any extra columns. These are stored on each frame and shown
                in Production Summary.
              </Text>

              <Flex gap={6} flexWrap="wrap">
                {OPTIONAL_FRAME_ATTRIBUTES.map((attr) => (
                  <Flex
                    key={attr.key}
                    flexDirection="column"
                    minW="220px"
                    flex={1}
                  >
                    <Text whiteSpace="nowrap" mb={2}>
                      {attr.label}
                    </Text>
                    <Box width="100%" minW="220px">
                      <SelectWithAction
                        items={[FRAME_NONE_OPTION, ...headers]}
                        value={attributeCols[attr.key] || FRAME_NONE_OPTION}
                        onSelect={(value: string) => {
                          setAttributeCols((prev) => {
                            const next = { ...prev };
                            if (value === FRAME_NONE_OPTION) {
                              delete next[attr.key];
                            } else {
                              next[attr.key] = value;
                            }
                            return next;
                          });
                        }}
                        placeholder="Choose column"
                      />
                    </Box>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Card>
        </>
      )}

      <Separator />
      <Box>
        <Button
          size="sm"
          type="submit"
          disabled={!workbookInfo?.sheets?.length}
          onClick={handleSubmit}
          data-testid="tutorial-bom-map-confirm"
        >
          Save Mapping
        </Button>
      </Box>
    </Flex>
  );
};

const FrameMappingSummaryCard = ({
  bomMapping,
}: {
  bomMapping: BomMappingForm;
}) => {
  const mapping = bomMapping.mapping ?? {};
  const attributeEntries = Object.entries(mapping).filter(
    ([key]) => key !== 'frame_number' && key !== 'quantity',
  );

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Flex gap={6} flexWrap="wrap" alignItems="flex-end">
            <Flex flexDirection="column" minW="260px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Frame Number Column
              </Text>
              <Input
                label=""
                minWidth="full"
                value={mapping.frame_number?.source || ''}
                disabled
              />
            </Flex>
            <Flex flexDirection="column" minW="260px" flex={1}>
              <Text whiteSpace="nowrap" mb={2}>
                Quantity Column
              </Text>
              <Input
                label=""
                minWidth="full"
                value={mapping.quantity?.source || ''}
                disabled
              />
            </Flex>
          </Flex>

          <Flex gap={2} flexWrap="wrap">
            <Badge bg="border.emphasized" color="fg">
              <Text>Sheet: {bomMapping.sheet_name || '-'}</Text>
            </Badge>
            <Badge bg="border.emphasized" color="fg">
              <Text>Header Row: {(bomMapping.header_row_index ?? 0) + 1}</Text>
            </Badge>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Flex flexDirection="column" gapY={2}>
          <Text fontSize="sm" fontWeight="bold">
            Optional Attributes
          </Text>
          {!attributeEntries.length ? (
            <Text fontSize="sm" color="fg.subtle">
              No attribute columns mapped.
            </Text>
          ) : (
            attributeEntries.map(([key, item]) => (
              <Flex key={key} justifyContent="space-between">
                <Text fontSize="sm">{key}</Text>
                <Text fontSize="sm" color="fg.subtle">
                  {item?.source || '-'}
                </Text>
              </Flex>
            ))
          )}
        </Flex>
      </Card>
    </Flex>
  );
};

const ViewFrameBomMappingOverlayDialogue = ({
  overlayId,
  bomMapping,
}: {
  overlayId: string;
  bomMapping: BomMappingForm;
}) => {
  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={2}>
          <Text fontSize="sm" fontWeight="bold">
            Mapping name
          </Text>
          <Input label="" minWidth="full" value={bomMapping.name || ''} disabled />
        </Flex>
      </Card>

      <FrameMappingSummaryCard bomMapping={bomMapping} />

      <Flex justifyContent="flex-end">
        <Button onClick={() => OverlayDialog.close(overlayId)}>Close</Button>
      </Flex>
    </Flex>
  );
};

const EditFrameBomMappingOverlayDialogue = ({
  overlayId,
  bomMapping,
}: {
  overlayId: string;
  bomMapping: BomMappingForm;
}) => {
  const [name, setName] = useState(bomMapping.name || '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [patchBomMapping] = ReleasesApi.Api.usePatchBomMappingMutation();

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError('Mapping name is required');
      return;
    }
    if (!bomMapping.id) {
      toasterApi({
        type: 'error',
        status: 400,
        code: 'Error',
        message: 'Missing BOM mapping id',
      });
      return;
    }

    try {
      await patchBomMapping({
        id: bomMapping.id,
        body: {
          name,
          mapping: bomMapping.mapping,
          mapping_type: ReleasesType.bomMappingType.Frame,
          sheet_name: bomMapping.sheet_name,
          header_row_index: bomMapping.header_row_index,
        },
      }).unwrap();

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'Frame BOM Mapping updated successfully',
      });

      OverlayDialog.close(overlayId);
    } catch (err: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message:
          err?.data?.detail ||
          err?.data?.message ||
          'Could not update Frame BOM Mapping',
      });
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={2}>
          <Text fontSize="sm" fontWeight="bold">
            Mapping name <Span color="fg.error">*</Span>
          </Text>
          <Input
            label=""
            minWidth="full"
            value={name}
            onChange={(value: string) => {
              setNameError(null);
              setName(value);
            }}
          />
          {nameError && <Alert status="error">{nameError}</Alert>}
          <Text fontSize="xs" color="fg.subtle">
            To change the mapped columns, create a new mapping from the source
            file.
          </Text>
        </Flex>
      </Card>

      <FrameMappingSummaryCard bomMapping={bomMapping} />

      <Flex justifyContent="flex-end" gap={2}>
        <Button variant="outline" onClick={() => OverlayDialog.close(overlayId)}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Save Changes</Button>
      </Flex>
    </Flex>
  );
};